/**
 * Control Portal - Global Billing Page
 * Platform-wide billing overview and revenue metrics
 */
import { useState, useEffect } from 'react';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  CreditCard, TrendingUp, DollarSign, Users, AlertTriangle,
  Download, RefreshCw, Loader2, Building2, Calendar
} from 'lucide-react';

export default function GlobalBillingPage() {
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState({
    summary: {
      totalMRR: 0,
      totalARR: 0,
      activeSubscriptions: 0,
      pendingInvoices: 0,
      overdueAmount: 0
    },
    recentInvoices: [],
    tenantSubscriptions: []
  });

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      const [summaryRes, invoicesRes, subscriptionsRes] = await Promise.all([
        controlApi.getGlobalBillingSummary(),
        controlApi.getGlobalInvoices(),
        controlApi.getGlobalSubscriptions()
      ]);
      
      setBillingData({
        summary: summaryRes.data,
        recentInvoices: invoicesRes.data,
        tenantSubscriptions: subscriptionsRes.data
      });
    } catch (error) {
      console.error('Failed to load billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      overdue: 'bg-red-500/20 text-red-400 border-red-500/30',
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      past_due: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return <Badge className={styles[status]}>{status.replace('_', ' ')}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-blue-500" />
            Global Billing
          </h1>
          <p className="text-slate-400 mt-1">Platform-wide revenue and subscription management</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="border-slate-700 text-slate-300">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Revenue Summary */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Monthly Revenue</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(billingData.summary.totalMRR)}</p>
          <p className="text-green-400 text-sm mt-1">+12% vs last month</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Annual Revenue</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(billingData.summary.totalARR)}</p>
          <p className="text-blue-400 text-sm mt-1">Projected ARR</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-purple-400" />
            <span className="text-slate-400 text-sm">Active Subscriptions</span>
          </div>
          <p className="text-2xl font-bold text-white">{billingData.summary.activeSubscriptions}</p>
          <p className="text-slate-500 text-sm mt-1">Paying customers</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-amber-400" />
            <span className="text-slate-400 text-sm">Pending Invoices</span>
          </div>
          <p className="text-2xl font-bold text-white">{billingData.summary.pendingInvoices}</p>
          <p className="text-amber-400 text-sm mt-1">Awaiting payment</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-slate-400 text-sm">Overdue Amount</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(billingData.summary.overdueAmount)}</p>
          <p className="text-red-400 text-sm mt-1">Requires attention</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">Recent Invoices</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead className="text-slate-400">Invoice</TableHead>
                <TableHead className="text-slate-400">Tenant</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billingData.recentInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                billingData.recentInvoices.map((inv) => (
                  <TableRow key={inv.id} className="border-slate-800">
                    <TableCell className="text-white font-mono text-sm">{inv.id}</TableCell>
                    <TableCell className="text-slate-300">{inv.tenant}</TableCell>
                    <TableCell className="text-slate-300">{formatCurrency(inv.amount)}</TableCell>
                    <TableCell>{getStatusBadge(inv.status)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Tenant Subscriptions */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">Active Subscriptions</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead className="text-slate-400">Tenant</TableHead>
                <TableHead className="text-slate-400">Plan</TableHead>
                <TableHead className="text-slate-400">MRR</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billingData.tenantSubscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                    No subscriptions found
                  </TableCell>
                </TableRow>
              ) : (
                billingData.tenantSubscriptions.map((sub, idx) => (
                  <TableRow key={idx} className="border-slate-800">
                    <TableCell className="text-white">{sub.tenant}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-slate-300 border-slate-600">
                        {sub.plan}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300">{formatCurrency(sub.mrr)}</TableCell>
                    <TableCell>{getStatusBadge(sub.status)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
